import { useState, useEffect } from "react";
import { MapPin, Mail, Phone, Send } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useSelectTheme, useSetSelectTheme } from "@/contexts/SelectThemeContext";

/**
 * Public contact page — form + office / email / phone info blocks.
 * Available on both tiers via /contact. Tier-aware styling: the form
 * card uses the Select cream palette when the shell theme is on, and
 * the Supply bone palette otherwise.
 *
 * Mailing address is currently a placeholder — swap the `OFFICE_LINES`
 * constant below with the real business address when it's supplied.
 */

// Real business mailing address (iter148j).
const OFFICE_LINES = ["1617 17th Avenue South", "Nashville, TN 37212"];
const SUPPORT_EMAIL = "support@bulkbuykits.com";
const SUPPORT_PHONE_DISPLAY = "(931) 327-0458";
const SUPPORT_PHONE_TEL = "+19313270458";

export default function Contact() {
  // iter148j — Contact inherits the last-visited tier's palette so a buyer
  // navigating from a Select-themed catalog gets the cream frame here too.
  // Catalog writes `bbk_last_tier` to localStorage on tier change; SelectStory
  // writes 'select' on mount.
  const [tierPref] = useState(() => {
    try { return (localStorage.getItem("bbk_last_tier") || "supply").toLowerCase(); }
    catch { return "supply"; }
  });
  useSetSelectTheme(tierPref === "select");

  const { isSelect } = useSelectTheme();
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      setSubmitted(true);
      setForm({ first_name: "", last_name: "", email: "", phone: "", message: "" });
      toast.success("Message sent. We'll reply within one business day.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't send. Please try again or email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  // Tier-aware tokens
  const cardBg = isSelect ? "#FBF3DD" : "#FAF7EF";
  const cardBorder = isSelect ? "rgba(221,169,78,0.55)" : "rgba(28,25,23,0.10)";
  const accent = isSelect ? "#6E521A" : "#4A0E17";
  const ink = isSelect ? "#1B1614" : "#1C1917";
  const kicker = isSelect ? "text-[#6E521A]" : "text-oxblood";
  const submitBg = isSelect ? "#DDA94E" : "#1C1917";
  const submitFg = isSelect ? "#000" : "#FFF";
  const iconTint = isSelect ? "#DDA94E" : "#4A0E17";
  const iconBg = isSelect ? "rgba(221,169,78,0.15)" : "rgba(74,14,23,0.06)";

  const inputCls = `w-full rounded-md border px-4 py-3 text-[15px] leading-tight transition-colors focus:outline-none focus:ring-2 ${
    isSelect
      ? "bg-white/70 border-[#DDA94E]/40 focus:border-[#DDA94E] focus:ring-[#DDA94E]/25 text-[#1B1614]"
      : "bg-white border-borderSubtle focus:border-charcoal focus:ring-charcoal/15 text-charcoal"
  }`;
  const labelCls = "block text-[11px] uppercase tracking-[0.22em] font-mono mb-1.5";

  return (
    <div className="max-w-[1200px] mx-auto px-6 lg:px-12 py-14 lg:py-20" data-testid="contact-page">
      <p className={`text-[12px] uppercase tracking-[0.32em] font-mono mb-3 ${kicker}`}>Contact</p>
      <h1
        className="font-serif text-4xl md:text-5xl tracking-tight leading-[1.05] mb-4"
        style={{ color: ink }}
        data-testid="contact-headline"
      >
        Feel free to reach out{" "}
        <span className="italic" style={{ color: accent }}>anytime</span>.
      </h1>
      <p className="max-w-2xl text-[15px] md:text-[16px] leading-relaxed mb-12" style={{ color: isSelect ? "#4A4340" : "#57534E" }}>
        Questions about a product, a pending order, or wholesale access? Send us a note below or reach us directly by
        email or text — we reply within one business day.
      </p>

      {/* Form card */}
      <div
        className="rounded-2xl p-6 md:p-8 mb-14"
        style={{ background: cardBg, border: `1px solid ${cardBorder}`, boxShadow: "0 24px 60px -40px rgba(27,22,20,0.35)" }}
        data-testid="contact-form-card"
      >
        {submitted ? (
          <div className="py-10 text-center" data-testid="contact-form-success">
            <p className={`text-[12px] uppercase tracking-[0.28em] font-mono mb-3 ${kicker}`}>Message received</p>
            <p className="font-serif text-2xl md:text-3xl tracking-tight mb-3" style={{ color: ink }}>
              Thanks — we'll be in touch soon.
            </p>
            <p className="text-[14px]" style={{ color: isSelect ? "#4A4340" : "#57534E" }}>
              You'll typically hear back within one business day.
            </p>
            <button
              type="button"
              onClick={() => setSubmitted(false)}
              className={`mt-6 inline-block text-[12px] uppercase tracking-[0.24em] font-mono underline ${isSelect ? "text-[#6E521A] hover:text-[#DDA94E]" : "text-charcoal hover:text-oxblood"}`}
              data-testid="contact-send-another"
            >
              Send another message
            </button>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="contact-form">
            <div>
              <label className={labelCls} style={{ color: ink }}>First Name *</label>
              <input
                type="text"
                required
                value={form.first_name}
                onChange={set("first_name")}
                className={inputCls}
                placeholder="First"
                data-testid="contact-first-name"
              />
            </div>
            <div>
              <label className={labelCls} style={{ color: ink }}>Last Name *</label>
              <input
                type="text"
                required
                value={form.last_name}
                onChange={set("last_name")}
                className={inputCls}
                placeholder="Last"
                data-testid="contact-last-name"
              />
            </div>
            <div>
              <label className={labelCls} style={{ color: ink }}>Email Address *</label>
              <input
                type="email"
                required
                value={form.email}
                onChange={set("email")}
                className={inputCls}
                placeholder="you@example.com"
                data-testid="contact-email"
              />
            </div>
            <div>
              <label className={labelCls} style={{ color: ink }}>Phone Number</label>
              <input
                type="tel"
                value={form.phone}
                onChange={set("phone")}
                className={inputCls}
                placeholder="Optional"
                data-testid="contact-phone"
              />
            </div>
            <div className="md:col-span-2">
              <label className={labelCls} style={{ color: ink }}>Message *</label>
              <textarea
                required
                value={form.message}
                onChange={set("message")}
                rows={6}
                className={`${inputCls} resize-y`}
                placeholder="Tell us how we can help…"
                data-testid="contact-message"
              />
            </div>
            <div className="md:col-span-2 pt-2">
              <button
                type="submit"
                disabled={submitting}
                className="w-full inline-flex items-center justify-center gap-2 rounded-full py-3.5 text-[12px] uppercase tracking-[0.28em] font-mono font-semibold transition-all hover:brightness-[1.08] disabled:opacity-60"
                style={{ background: submitBg, color: submitFg }}
                data-testid="contact-submit"
              >
                {submitting ? "Sending…" : (<><Send size={14} strokeWidth={1.8} /> Submit</>)}
              </button>
            </div>
          </form>
        )}
      </div>

      {/* Info blocks — Office / Email / Phone */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-10" data-testid="contact-info-blocks">
        <InfoBlock
          icon={<MapPin size={22} strokeWidth={1.5} style={{ color: iconTint }} />}
          iconBg={iconBg}
          kicker="Our Office"
          kickerCls={kicker}
          ink={ink}
          testid="contact-info-office"
        >
          {OFFICE_LINES.map((line, i) => (
            <p key={i} className="text-[15px] leading-relaxed" style={{ color: ink }}>{line}</p>
          ))}
        </InfoBlock>

        <InfoBlock
          icon={<Mail size={22} strokeWidth={1.5} style={{ color: iconTint }} />}
          iconBg={iconBg}
          kicker="Email Address"
          kickerCls={kicker}
          ink={ink}
          testid="contact-info-email"
        >
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className={`text-[15px] hover:underline ${isSelect ? "text-[#6E521A]" : "text-oxblood"}`}
            data-testid="contact-info-email-link"
          >
            {SUPPORT_EMAIL}
          </a>
        </InfoBlock>

        <InfoBlock
          icon={<Phone size={22} strokeWidth={1.5} style={{ color: iconTint }} />}
          iconBg={iconBg}
          kicker="Phone Number"
          kickerCls={kicker}
          ink={ink}
          testid="contact-info-phone"
        >
          <a
            href={`sms:${SUPPORT_PHONE_TEL}`}
            className={`text-[15px] hover:underline ${isSelect ? "text-[#6E521A]" : "text-oxblood"}`}
            data-testid="contact-info-phone-link"
          >
            {SUPPORT_PHONE_DISPLAY}
          </a>
          <p className="text-[11px] uppercase tracking-[0.22em] font-mono mt-1" style={{ color: isSelect ? "#78716C" : "#78716C" }}>
            Text only
          </p>
        </InfoBlock>
      </div>
    </div>
  );
}

function InfoBlock({ icon, iconBg, kicker, kickerCls, ink, children, testid }) {
  return (
    <div className="text-center" data-testid={testid}>
      <div
        className="mx-auto mb-4 w-12 h-12 rounded-full flex items-center justify-center"
        style={{ background: iconBg }}
      >
        {icon}
      </div>
      <p className={`text-[11px] uppercase tracking-[0.28em] font-mono mb-2 ${kickerCls}`}>
        {kicker}
      </p>
      <div className="space-y-0.5" style={{ color: ink }}>
        {children}
      </div>
    </div>
  );
}
